import type { Market } from "@openbook-dex/openbook";
import type { OpenOrders } from "@openbook-dex/openbook/lib/market";
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  createAssociatedTokenAccountInstruction,
  getAssociatedTokenAddress,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token0.3.8";
import type { Connection, Signer, TransactionInstruction } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { Transaction } from "@solana/web3.js";
import { STEP_FEE_ADDRESS_USDC, STEP_FEE_ADDRESS_USDT, USDC_MINT, USDT_MINT } from "@stepfinance/common";
import type { UnsettledBalance } from "@stepfinance/dex";

import type { ConnectedWallet } from "../hooks/wallet/useConnectedWallet";
import { findOrCreateAccountByMint } from "./account";

export async function createTokenAccountTransaction({
  connection,
  wallet,
  mintPublicKey,
}: {
  connection: Connection;
  wallet: ConnectedWallet;
  mintPublicKey: PublicKey;
}): Promise<{
  transaction: Transaction;
  signer: Signer | undefined;
  newAccountPubkey: PublicKey;
}> {
  const signers: Signer[] = [];
  const instructions: TransactionInstruction[] = [];

  const walletPubKey = wallet.publicKey;

  const newOrExistingAccount = await findOrCreateAccountByMint(
    connection,
    walletPubKey,
    walletPubKey,
    instructions,
    mintPublicKey,
  );

  const transaction = new Transaction();
  instructions.forEach((i) => transaction.add(i));

  return {
    transaction,
    signer: signers.length > 0 ? signers[0] : undefined,
    newAccountPubkey: newOrExistingAccount,
  };
}

const findOrCreateSettleFundsAccount = async (
  mint: PublicKey,
  connection: Connection,
  walletPublicKey: PublicKey,
  createdAccounts: Set<string>,
): Promise<{ ata: PublicKey; transaction?: Transaction }> => {
  const associatedAddress = await getAssociatedTokenAddress(
    mint,
    walletPublicKey,
    false,
    TOKEN_PROGRAM_ID,
    ASSOCIATED_TOKEN_PROGRAM_ID,
  );

  if (!createdAccounts.has(associatedAddress.toBase58())) {
    const accountInfo = await connection.getAccountInfo(associatedAddress);
    // Returns createATA txn if token account is uninitialized
    if (accountInfo?.owner.toBase58() !== TOKEN_PROGRAM_ID.toBase58()) {
      createdAccounts.add(associatedAddress.toBase58());

      const transaction = new Transaction();
      transaction.add(
        createAssociatedTokenAccountInstruction(
          walletPublicKey,
          associatedAddress,
          walletPublicKey,
          mint,
          TOKEN_PROGRAM_ID,
          ASSOCIATED_TOKEN_PROGRAM_ID,
        ),
      );
      return { ata: associatedAddress, transaction: transaction };
    }
  }
  return { ata: associatedAddress };
};

export async function settleAllFundsTxs({
  connection,
  unsettledBalances,
  marketsMap,
  openOrdersMap,
}: {
  connection: Connection;
  unsettledBalances: UnsettledBalance[];
  marketsMap: Map<string, Market>;
  openOrdersMap: Map<string, OpenOrders>;
}) {
  if (!connection) {
    return;
  }
  const BATCH_SIZE = 4;

  const createdAccounts: Set<string> = new Set();

  const settleTransactions: {
    tx: Transaction;
    signers: Signer[];
  }[] = [];

  for (const unsettledBalance of unsettledBalances) {
    const market = marketsMap.get(unsettledBalance.marketAddress.toBase58());
    const openOrders = openOrdersMap.get(unsettledBalance.openOrdersAddress.toBase58());
    if (!market || !openOrders) {
      continue;
    }
    const baseMint = new PublicKey(unsettledBalance.baseMint);
    const quoteMint = new PublicKey(unsettledBalance.quoteMint);

    const baseAccount = await findOrCreateSettleFundsAccount(
      baseMint,
      connection,
      unsettledBalance.owner,
      createdAccounts,
    );
    const quoteAccount = await findOrCreateSettleFundsAccount(
      quoteMint,
      connection,
      unsettledBalance.owner,
      createdAccounts,
    );

    baseAccount.transaction && settleTransactions.push({ tx: baseAccount.transaction, signers: [] });
    quoteAccount.transaction && settleTransactions.push({ tx: quoteAccount.transaction, signers: [] });

    let referrerQuoteWallet: PublicKey | null = null;
    if (market.supportsReferralFees) {
      const usdcFeeAccount = STEP_FEE_ADDRESS_USDC;
      const usdtFeeAccount = STEP_FEE_ADDRESS_USDT;

      if (market.quoteMintAddress.equals(USDT_MINT)) {
        referrerQuoteWallet = usdtFeeAccount;
      } else if (market.quoteMintAddress.equals(USDC_MINT)) {
        referrerQuoteWallet = usdcFeeAccount;
      }
    }

    const { transaction: settleTransaction, signers: settleSigners } = await market.makeSettleFundsTransaction(
      connection,
      openOrders,
      baseAccount.ata,
      quoteAccount.ata,
      referrerQuoteWallet,
    );
    settleTransactions.push({ tx: settleTransaction, signers: settleSigners });
  }

  if (settleTransactions.length === 0) {
    return;
  }

  const transactions: Transaction[] = [];
  const signersArray: Signer[][] = [];
  const unsettledBalanceKeys: string[][] = [];

  for (let i = 0; i < settleTransactions.length; i += BATCH_SIZE) {
    const transaction = new Transaction();
    const signersChunk: Signer[] = [];
    const signers: Signer[] = [];
    const keys: string[] = [];

    settleTransactions.slice(i, i + BATCH_SIZE).forEach((c) => {
      transaction.add(c.tx);
      signersChunk.push(...c.signers);
    });

    signersChunk.forEach((signer) => {
      if (!signers.find((s) => s.publicKey.equals(signer.publicKey))) {
        signers.push(signer);
      }
    });

    unsettledBalanceKeys.push(keys);
    transactions.push(transaction);
    signersArray.push(signers);
  }
  return { txs: transactions, signers: signersArray };
}
